import React, { useState } from 'react';
import { useDrag } from 'react-dnd';
import {
    Box,
    Typography,
    MenuItem,
    IconButton,
    Menu,
    ListItemIcon,
    ListItemText,
    TextField,
    CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderIcon from '@mui/icons-material/Folder';
import { useTranslation } from 'react-i18next';

import { useChatSession } from '../contexts/ChatSessionContext';
import { useMessageHandler } from '../contexts/MessageContext';
import { ItemTypes } from '../constants/appConstants';
import ChatSessionContextMenu from './ChatSessionContextMenu';
import api from '../api/api';

const SessionItem = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'isHovered'
})(({ theme, isActive, isHovered }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.75, 1),
    margin: theme.spacing(0.25, 0),
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    backgroundColor: isActive
        ? theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : theme.palette.primary.light + '40'
        : isHovered
            ? theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)'
            : 'transparent',
    transition: 'background-color 0.2s ease',
    '&:hover': {
        backgroundColor: isActive
            ? theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.15)'
                : theme.palette.primary.light + '60'
            : theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.08)'
    }
}));

const SessionName = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'isActive'
})(({ theme, isActive }) => ({
    fontSize: '0.75rem',
    fontWeight: isActive ? 500 : 400,
    lineHeight: 1.5,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.mode === 'dark'
        ? isActive ? '#fff' : 'rgba(255, 255, 255, 0.8)'
        : isActive ? theme.palette.primary.main : theme.palette.text.primary,
    flexGrow: 1,
}));

const EditorField = styled(TextField)(({ theme }) => ({
    '& .MuiInputBase-input': {
        padding: theme.spacing(0.5, 1),
        fontSize: '0.875rem',
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius,
    }
}));

const ChatSessionItem = ({
    session,
    isInProject = false,
    isHovered = false,
    isCurrentSession = false,
    onSessionClick,
    onClose,
    onEngineChange
}) => {
    const { t } = useTranslation();
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [moveToProjectMenuAnchor, setMoveToProjectMenuAnchor] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState(session.name);
    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.CHAT_SESSION,
        item: { sessionId: session.sessionId, sessionName: session.name },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging()
        })
    }));

    const {
        projects = [],
        moveSessionToProject,
        loadChatSession,
        fetchChatSessions,
    } = useChatSession();

    const {
        isProcessing,
        setIsProcessing,
        isPlaybackActive,
        isTranscribing,
        isLoadingSession,
        setMessages,
        currentSession,
        setCurrentSession,
        newChatSession,
        showDialog,
        isInteracting,
    } = useMessageHandler();

    const handleMenuClick = (event) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleMoveToProjectClick = (event) => {
        event.stopPropagation();
        setMoveToProjectMenuAnchor(event.currentTarget);
    };

    const handleCloseMoveToProjectMenu = () => {
        setMoveToProjectMenuAnchor(null);
    };

    const handleMoveToProject = async (targetProjectId) => {
        if (isInteracting) return;

        try {
            setIsProcessing(true); // Set processing flag before API call
            await moveSessionToProject(session.sessionId, targetProjectId);
        } catch (error) {
            console.error('Error moving session to project:', error);
        } finally {
            setIsProcessing(false); // Reset flag regardless of success/failure
            handleCloseMoveToProjectMenu();
            handleMenuClose();
        }
    };

    const handleRemoveFromProject = async () => {
        await moveSessionToProject(session.sessionId, null);
        handleMenuClose();
    };

    const handleRenameClick = () => {
        setIsEditing(true);
        setEditedName(session.name);
        handleMenuClose();
    };

    const handleRenameConfirm = async () => {
        if (editedName.trim() !== '') {
            try {
                // Use API directly instead of relying on a context function
                await api.put(`${process.env.REACT_APP_API_BASE_URL}/api/chat/sessions/${session.sessionId}`, {
                    newName: editedName.trim()
                });

                // After successful rename, refresh the chat sessions
                if (typeof fetchChatSessions === 'function') {
                    await fetchChatSessions();
                }

                setIsEditing(false);
            } catch (error) {
                console.error('Error renaming session:', error);
                // Revert to original name on error
                setEditedName(session.name);
                setIsEditing(false);
            }
        } else {
            // If empty name, revert to original
            setEditedName(session.name);
            setIsEditing(false);
        }
    };

    const handleDeleteClick = async () => {
        // Close the menu first
        handleMenuClose();

        // Show confirmation dialog using the MessageContext's showDialog
        if (typeof showDialog === 'function') {
            showDialog(
                t('chatSession.delete.title'),
                t('chatSession.delete.confirmWithName', { name: session.name }),
                async () => {
                    try {
                        const wasCurrentSession = currentSession === session.sessionId;

                        // Use API directly to delete the chat session
                        await api.delete(`${process.env.REACT_APP_API_BASE_URL}/api/chat/sessions/${session.sessionId}`);

                        // If we were on this session, reset current session and messages
                        if (wasCurrentSession) {
                            setCurrentSession(null);
                            setMessages([]);

                            // If there's a newChatSession function, call it
                            if (typeof newChatSession === 'function') {
                                await newChatSession();
                            }
                        }

                        // Refresh the chat sessions list
                        if (typeof fetchChatSessions === 'function') {
                            await fetchChatSessions();
                        }
                    } catch (error) {
                        console.error('Error deleting chat session:', error);
                    }
                }
            );
        }
    };

    const handleExportPDF = async () => {
        try {
            const response = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/chat/sessions/${session.sessionId}/export`,
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `chat-${session.sessionId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);

            handleMenuClose();
        } catch (error) {
            console.error('Error exporting chat to PDF:', error);
        }
    };

    const handleClick = async () => {
        if (isProcessing || isLoadingSession || isPlaybackActive || isTranscribing) return;

        if (typeof onSessionClick === 'function') {
            onSessionClick(session.sessionId);
        } else {
            try {
                setCurrentSession(session.sessionId);
                const sessionData = await loadChatSession(session.sessionId);
                if (sessionData?.conversationHistory) {
                    setMessages(sessionData.conversationHistory);
                }

                // Update the engine selection
                if (sessionData?.engine && typeof onEngineChange === 'function') {
                    onEngineChange(sessionData.engine);
                  }

                // Close menu panel on mobile
                const isMobile = window.innerWidth < 600;
                if (isMobile && typeof onClose === 'function') {
                    onClose();
                }
            } catch (error) {
                console.error('Error loading session:', error);
            }
        }
    };

    if (isEditing) {
        return (
            <EditorField
                fullWidth
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onBlur={handleRenameConfirm}
                onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                        handleRenameConfirm();
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        setIsEditing(false);
                        setEditedName(session.name);
                    }
                }}
                autoFocus
                disabled={isProcessing || isPlaybackActive || isTranscribing}
                sx={{ margin: 0.5 }}
                data-testid={`chat-rename-input-${session.sessionId}`}
            />
        );
    }

    return (
        <div ref={drag} style={{ opacity: isDragging ? 0.5 : 1, width: '100%' }}>
            <SessionItem
                data-testid={`chat-session-${session.sessionId}`}
                isActive={isCurrentSession}
                isHovered={isHovered}
                onClick={handleClick}
                disabled={isInteracting}
            >
                <SessionName isActive={isCurrentSession}>
                    {session.name}
                </SessionName>

                {isLoadingSession && isCurrentSession && (
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                )}

                <IconButton
                    size="small"
                    onClick={handleMenuClick}
                    data-testid={`chat-menu-button-${session.sessionId}`}
                    sx={{
                        padding: 0.5,
                        marginLeft: 0.5,
                        opacity: isHovered ? 1 : 0.7,
                        color: theme => theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.6)',
                    }}
                    disabled={isProcessing || isPlaybackActive || isTranscribing}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>

            </SessionItem>

            <ChatSessionContextMenu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                isInProject={isInProject}
                onRename={handleRenameClick}
                onDelete={handleDeleteClick}
                onMoveToProjectClick={handleMoveToProjectClick}
                onRemoveFromProject={handleRemoveFromProject}
                onExport={handleExportPDF}
                disabled={isProcessing || isPlaybackActive || isTranscribing || isLoadingSession}
            />

            {/* Move to Project Menu */}
            {!isInProject && (
                <Menu
                    anchorEl={moveToProjectMenuAnchor}
                    open={Boolean(moveToProjectMenuAnchor)}
                    disabled={isInteracting}
                    onClose={() => {
                        handleCloseMoveToProjectMenu();
                        // Don't close the main menu
                    }}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right', // Position to the right of the first menu
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left', // Coming from the left
                    }}
                >
                    {projects && projects.map(project => (
                        <MenuItem
                            key={project.id}
                            onClick={() => handleMoveToProject(project.id)}
                            disabled={isProcessing || isPlaybackActive || isTranscribing || isLoadingSession}
                        >
                            <ListItemIcon>
                                <FolderIcon sx={{ color: project.color }} />
                            </ListItemIcon>
                            <ListItemText primary={project.name} />
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </div>
    );
};

export default ChatSessionItem;